/* RESPONSIBLE TEAM: team-automation-foundations */
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import Route from '@ember/routing/route';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import type FinAgentTypeService from 'embercom/services/fin-agent-type-service';
import type { FinAgentType } from 'embercom/services/fin-agent-type-service';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { FinGettingStartedState } from 'embercom/lib/reporting/fin-getting-started-states';
import { get } from 'embercom/lib/ajax';
// Adding a model type as described here:
// https://docs.ember-cli-typescript.com/ember/routes
type Resolved<P> = P extends Promise<infer T> ? T : P;
export type FinAIAgentSetupRouteModel = Resolved<
  ReturnType<AutomationFinAiAgentSetupRoute['model']>
>;

interface AgentDeploymentStatuses {
  support: boolean;
  sales: boolean;
  ecommerce: boolean;
}

export default class AutomationFinAiAgentSetupRoute extends Route {
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare permissionsService: $TSFixMe;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare finAgentTypeService: FinAgentTypeService;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare router: RouterService;
  @service declare appService: any;

  gettingStarted = new FinGettingStartedState(this);
  analytics = {
    section: 'automation',
    place: 'fin-ai-agent.setup',
  };

  async beforeModel(transition: Transition) {
    if (this.appService.app.canUseSdrInformationArchitectureChange) {
      let agentSelection = this.finAgentTypeService.currentAgentType;
      if (agentSelection === 'all-roles' && this.appService.app.canUseFeature('all-roles-page')) {
        let deploymentStatuses: AgentDeploymentStatuses | null = null;
        try {
          deploymentStatuses = (await get('/teammate_app/agent_deployment_statuses', {
            app_id: this.appService.app.id,
          })) as AgentDeploymentStatuses;
        } catch (e: unknown) {
          console.error(e);
        }

        if (deploymentStatuses?.support) {
          // Fall through to the analyze.performance / analyze.get-started / content logic below.
          this.syncAgentType('support');
        } else if (deploymentStatuses?.sales) {
          this.syncAgentType('sales');
          this.router.replaceWith('apps.app.automation.fin-ai-agent.sales');
          return;
        } else if (deploymentStatuses?.ecommerce && this.appService.app.canUseFinEcommerceAgent) {
          this.syncAgentType('ecommerce');
          this.router.replaceWith('apps.app.automation.fin-ai-agent.ecommerce');
          return;
        } else {
          this.router.replaceWith('apps.app.automation.fin-ai-agent.all-roles');
          return;
        }
      }
      if (agentSelection === 'sales') {
        this.router.replaceWith('apps.app.automation.fin-ai-agent.sales');
        return;
      }
      if (agentSelection === 'ecommerce' && this.appService.app.canUseFinEcommerceAgent) {
        this.router.replaceWith('apps.app.automation.fin-ai-agent.ecommerce');
        return;
      }
    }

    let currentURLBeforeLoadingData = this.router.currentURL;
    let gettingStartedState = null;
    try {
      gettingStartedState = await this.gettingStarted.getState();
    } catch (e) {
      console.error(e);
    }
    await this.loadAiAgentSetupService();
    if (currentURLBeforeLoadingData !== this.router.currentURL) {
      return;
    }
    if (transition.to.queryParams?.activeTab) {
      if (transition.to.queryParams?.activeTab === 'personality') {
        this.router.replaceWith('apps.app.automation.fin-ai-agent.guidance');
        return;
      }
      if (transition.to.queryParams?.activeTab.startsWith('set-live-for-')) {
        let tab = transition.to.queryParams?.activeTab.split('set-live-for-')[1];
        this.router.replaceWith(`apps.app.automation.fin-ai-agent.deploy.${tab}`);
        return;
      }
    }
    let canSeeReports = this.permissionsService.currentAdminCan('can_access_reporting');
    if (gettingStartedState) {
      this.router.replaceWith('apps.app.automation.fin-ai-agent.analyze.get-started');
    } else if (this.aiAgentSetupService.isFinLive && canSeeReports) {
      this.router.replaceWith('apps.app.automation.fin-ai-agent.analyze.performance');
    } else if (
      !this.aiAgentSetupService.isFinLive &&
      this.appService.app.canUseFeature('fin-first-new-fin-step')
    ) {
      this.router.replaceWith('apps.app.automation.fin-ai-agent.analyze.performance');
    } else {
      this.router.replaceWith('apps.app.automation.fin-ai-agent.content');
    }
    return;
  }

  get titleToken(): string {
    return this.intl.t('ai-agent.sub-menu.fin-overview.title');
  }

  async loadAiAgentSetupService() {
    await this.aiAgentSetupService.load();
  }

  private syncAgentType(agentType: FinAgentType) {
    this.finAgentTypeService.setCurrentAgentType(agentType);
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('fin-agent-type:changed', {
      agentType,
    });
  }

  @action
  didTransition() {
    this.intercomEventService.trackEvent('fin-automation-page-visited');
  }
}
